<template>
  <div>
    <b-row>
      <b-col
        class="d-flex justify-content-between filter-heading-container"
        cols="12"
      >
        <div class="d-flex align-items-center">
          <h6 class="filter-heading ps-1 mb-0 d-flex flex-column justify-content-center">
            Filters
          </h6>
          <slot name="hint" />
        </div>

        <b-button
          variant="flat-primary"
          size="sm"
          @click="clearFilters()"
        >
          CLEAR
        </b-button>
      </b-col>
    </b-row>

    <!-- Filters' Card -->
    <div v-if="filterDisplayLoading">
      <loading-container />
    </div>
    <b-card v-else>
      <div
        class="mb-2 d-flex flex-column"
      >
        <h6>Date Constraint</h6>
        <v-select
          v-model="constraints.dateType"
          :options="dateTypeOptions"
          :reduce="option => option.value"
          :clearable="false"
          class="mb-1 date-type-select"
        />
        <b-form-datepicker
          v-if="constraints.dateType !== 'None'"
          v-model="constraints.dateFrom"
          class="mb-1"
          :placeholder="constraints.dateType === 'Live' ? 'As at' : 'From'"
          :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
          locale="en-GB"
          :min="dateValidation.dateFrom.min"
          :max="dateValidation.dateFrom.max"
          reset-button
          :reset-value="''"
          label-reset-button="Clear"
        />
        <b-form-datepicker
          v-if="constraints.dateType !== 'Live' && constraints.dateType !== 'None'"
          v-model="constraints.dateTo"
          class="mb-1"
          placeholder="To"
          :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
          locale="en-GB"
          :min="dateValidation.dateTo.min"
          :max="dateValidation.dateTo.max"
          reset-button
          :reset-value="''"
          label-reset-button="Clear"
        />
      </div>

      <template v-for="category in formattedFilterDisplay">
        <product-filter-slider
          v-if="category.slider"
          :key="`slider-${category.categoryKey}`"
          :value="getSliderValue(category.categoryKey, category.slider.items, category.slider.customLabels)"
          :constraint-key="category.categoryKey"
          :title="category.categoryLabel"
          :data-points="category.slider.items"
          :custom-labels="category.slider.customLabels"
          :suffix="category.slider.suffix"
          :direction="category.slider.direction"
          @input="updateSliderValue(category.categoryKey, $event)"
        />
        <product-filter-category
          v-else
          :key="`category-${category.categoryKey}`"
          :category="category"
          :filter="filter"
          :is-autocall-tab-active="isAutocallTabActive"
          v-on="$listeners"
        />
      </template>
    </b-card>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BButton, BFormDatepicker,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import ProductFilterCategory from '@/views/components/ProductFilterCategory.vue'
import ProductFilterSlider from '@/views/components/ProductFilterSlider.vue'
import vSelect from 'vue-select'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BFormDatepicker,
    ProductFilterCategory,
    ProductFilterSlider,
    LoadingContainer,
    vSelect,
  },
  props: {
    filterDisplayLoading: {
      type: Boolean,
      default: false,
    },
    filterDisplay: {
      type: [Array],
      required: true,
    },
    filter: {
      type: [Object],
      required: true,
    },
    constraints: {
      type: [Object],
      required: true,
    },
    isAutocallTabActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sliderValues: {},
      isClearing: false,
      dateTypeOptions: [
        {
          label: 'None',
          value: 'None',
        },
        {
          label: 'Closing Date',
          value: 'closingdate',
        },
        {
          label: 'Strike Date',
          value: 'strikedate',
        },
        {
          label: 'Final Market Date',
          value: 'finalmarketdate',
        },
      ],
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isLoggedIn',
    ]),
    filterResult() {
      const result = {}
      Object.entries(this.filter).forEach(([key, value]) => {
        if (value.length) {
          result[key] = value
        }
      })
      return result
    },
    formattedFilterDisplay() {
      const formatted = this.filterDisplay.map(category => {
        // If it has a slider property, it's a slider category - return as is
        if (category.slider) return category
        // Otherwise it's a regular filter category - format the items
        const formattedCategory = { ...category }
        formattedCategory.items = category.items.map(item => ({
          ...item,
          html: `<span class="d-flex justify-content-between">${item.label} <span class="badge badge-light-primary filter-product-count">${item.count}</span></span>`,
        }))
        return formattedCategory
      })
      return formatted
    },
    nonSliderCategories() {
      return this.formattedFilterDisplay.filter(category => !category.slider)
    },
    dateValidation() {
      const dateValidation = {
        dateTo: {
          min: null,
          max: this.constraints.dateType === 'Matured' ? new Date() : null,
        },
        dateFrom: {
          min: null,
          max: this.constraints.dateType === 'Matured' || this.constraints.dateType === 'Live' ? new Date() : null,
        },
      }

      if (this.constraints.dateTo) {
        dateValidation.dateFrom.max = this.constraints.dateTo
      }

      if (this.constraints.dateFrom) {
        dateValidation.dateTo.min = this.constraints.dateFrom
      }

      return dateValidation
    },
  },
  watch: {
    isLoggedIn() {
      this.sliderValues = {}
    },
    'constraints.dateType': function (newVal) {
      switch (newVal) {
        case 'None':
          this.constraints.dateTo = ''
          this.constraints.dateFrom = ''
          break
        case 'Matured':
          if (new Date(this.constraints.dateTo) > ((new Date()).setHours(0, 0, 0, 0))) {
            this.constraints.dateTo = ''
          }
          if (new Date(this.constraints.dateFrom) > ((new Date()).setHours(0, 0, 0, 0))) {
            this.constraints.dateFrom = ''
          }
          break
        case 'Live':
          if (new Date(this.constraints.dateFrom) > ((new Date()).setHours(0, 0, 0, 0))) {
            this.constraints.dateFrom = ''
          }
          this.constraints.dateTo = ''
          break
        default:
      }

      this.$emit('constraint-change', this.constraints)
    },
    'constraints.dateFrom': function () {
      this.$emit('constraint-change', this.constraints)
    },
    'constraints.dateTo': function () {
      this.$emit('constraint-change', this.constraints)
    },
  },
  mounted() {
    // Listen for slider-constraint-change events from child sliders
    this.$on('slider-constraint-change', this.handleSliderConstraintChange)
    // Listen for slider-filter-change events to update filter selections
    this.$on('slider-filter-change', this.handleSliderFilterChange)
  },
  beforeDestroy() {
    this.$off('slider-constraint-change', this.handleSliderConstraintChange)
    this.$off('slider-filter-change', this.handleSliderFilterChange)
  },
  methods: {
    clearFilters() {
      this.isClearing = true

      // Clear the sliderValues object to reset all sliders
      this.sliderValues = {}

      // Emit clear-filter event to child sliders
      this.$emit('clear-filter', {})

      // Wait for all sliders to finish resetting before emitting filter-change
      this.$nextTick(() => {
        this.$emit('filter-change', {})
        this.$nextTick(() => {
          this.isClearing = false
        })
      })
    },
    handleSliderConstraintChange(sliderConstraints) {
      // Merge the slider constraints with existing constraints
      const updatedConstraints = {
        ...this.constraints,
        ...sliderConstraints,
      }
      this.$emit('constraint-change', updatedConstraints)
    },
    handleSliderFilterChange(payload) {
      // Create a copy of the current filter
      const updatedFilter = { ...this.filter }

      // If payload has values, add/update the filter; if empty, remove the filter
      if (payload.values && payload.values.length > 0) {
        updatedFilter[payload.categoryKey] = payload.values
      } else {
        // Remove the key entirely if values is empty (full range)
        delete updatedFilter[payload.categoryKey]
      }

      // Emit the updated filter directly (already filtered)
      this.$emit('filter-change', updatedFilter)
    },
    getSliderValue(categoryKey, dataPoints, customLabels = []) {
      // if we already have a user-set value, use it
      if (Object.prototype.hasOwnProperty.call(this.sliderValues, categoryKey)) {
        return this.sliderValues[categoryKey]
      }

      // compute full range ONLY when dataPoints is ready
      if (Array.isArray(dataPoints) && dataPoints.length > 0) {
        const numeric = dataPoints
          .map(v => Number(v.value ?? v))
          .filter(n => !Number.isNaN(n))

        if (numeric.length === 0) {
          // If no numeric points but have custom labels, create range for them
          if (customLabels.length > 0) {
            return [0, customLabels.length - 1]
          }
          return []
        }

        // Calculate interval for custom label positions
        const sorted = [...numeric].sort((a, b) => a - b)
        let interval = 1
        if (sorted.length >= 2) {
          const diffs = []
          for (let i = 1; i < sorted.length; i += 1) {
            diffs.push(sorted[i] - sorted[i - 1])
          }
          const validDiffs = diffs.filter(d => d > 0)
          interval = validDiffs.length > 0 ? Math.min(...validDiffs) : 1
        }

        const dataMin = Math.min(...numeric)
        const dataMax = Math.max(...numeric)

        // Extend range if custom labels exist
        let min = dataMin
        let max = dataMax

        if (customLabels && customLabels.length > 0) {
          customLabels.forEach(custom => {
            if (custom.position === 0) {
              // Left custom label
              min = dataMin - interval
            } else if (custom.position === 1) {
              // Right custom label
              max = dataMax + interval
            }
          })
        }

        return [min, max]
      }

      return []
    },
    updateSliderValue(categoryKey, value) {
      // Skip updates during clear operation to prevent unnecessary re-renders
      if (this.isClearing) {
        return
      }
      // Use Vue.set to ensure reactivity
      this.$set(this.sliderValues, categoryKey, value)
    },
  },
}
</script>

<style lang="scss" scoped>
.filter-heading-container {
  margin-bottom: 0.5rem;
}

.date-type-select::v-deep > div {
  min-height: 37px;
}
.date-type-select::v-deep > div > div > input {
  height: 0;
}

.date-type-select::v-deep > div > div > span {
  position: static !important;
}

.date-type-select::v-deep,
.date-type-select::v-deep * {
  cursor: pointer !important;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
